import { isset } from '@/legacy/utils/utils';
import React, { useCallback, useEffect, useRef } from 'react';
import { CallCallbackParams } from '../tours/tour-controller';
import { useSession } from '@/providers/session-provider';
import posthog, { Properties } from 'posthog-js';
import { useSwitchWorkspace } from '@/hooks/use-switch-workspace';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { LegacyPath } from './LegacyIframes';
import NotFound from '@/pages/public/NotFound';
import { usersPathFilterAccess } from '@/utils/users-path-filter';

let iframe: Window | null = null;
const legacyHost: string = import.meta.env.VITE_LEGACY_APP_ROOT as string;

const pathMatch: LegacyPath[] = [
    {
        oldPath: 'listening',
        newPath: 'listening',
        andieDescription:
            'This page is used to make social & web listening reports. Here you can create listening dashboards to see were your brand is mentioned.',
    },
];

export { pathMatch };

interface LegacySyncAction {
    id?: string;
    type?: string;
    value?: string | null;
    selector?: string | null;
    html?: string;
    rect?: CSSStyleDeclaration;
    tour?: string;
    status?: boolean;
    set?: Properties;
}

const LegacyIframe = ({
    showBackdrop,
    setShowBackdrop,
}: Readonly<{
    showBackdrop: boolean;
    setShowBackdrop: React.Dispatch<React.SetStateAction<boolean>>;
}>) => {
    const pathName = useLocation().pathname;
    const navigate = useNavigate();
    const target = legacyHost;

    const session = useSession();

    const reactPaths = ['/export', '/offers', '/discover', '/badges'];

    const excludePaths = ['competitors/monitoring'];

    const { switchWorkspace } = useSwitchWorkspace();
    // legacySyncActions is reached from the message listener's []-deps effect, which would pin
    // the first render's switchWorkspace forever. That identity changes (it depends on `t`,
    // among others), so keep a live reference rather than a stale capture.
    const switchWorkspaceRef = useRef(switchWorkspace);
    useEffect(() => {
        switchWorkspaceRef.current = switchWorkspace;
    });

    // The <iframe> is absent whenever the NotFound branch below renders, and this component
    // does not remount on path changes — so binding the window in a mount-once effect would
    // leave `iframe` null for the rest of the session once that branch is hit. A callback ref
    // rebinds on every attach/detach instead.
    const setIframeNode = useCallback((node: HTMLIFrameElement | null) => {
        iframe = node?.contentWindow ?? null;
    }, []);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (target.indexOf(event.origin) !== -1 && isset(event.data) && isset(event.data.path)) {
                let path = event.data.path.replace('#', '');

                path = getPagePath(path, 'new');
                if (isLegacy && pathName !== path) {
                    navigate(path, { replace: true });
                }

                legacySyncActions(isset(event.data.action, {}));
            }
        };

        window.addEventListener('message', handleMessage, false);

        return () => {
            window.removeEventListener('message', handleMessage, false);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isLegacy = (() => {
        for (const paths of pathMatch) {
            if (null != matchPath({ path: `/${paths.newPath}/*` }, pathName)) {
                return 'new';
            } else if (null != matchPath({ path: `/${paths.oldPath}/*` }, pathName)) {
                return 'old';
            }
        }
        return false;
    })();

    const legacySyncActions = (data: LegacySyncAction) => {
        switch (isset(data.type, '')) {
            case 'switchAccount':
                if (data.id) {
                    switchWorkspaceRef.current({ idLegacy: parseInt(data.id) });
                }
                break;
            case 'event':
                document.getElementById('root')?.click();
                break;
            case 'fullscreen':
                if (document.querySelector('.Top.Bar')) {
                    document.querySelector<HTMLElement>('.Top.Bar')!.style.display = isset(data.value, false)
                        ? 'none'
                        : '';
                }
                break;
            case 'supportChat':
                (window as any).toggleChat(isset(data.value, true)); // eslint-disable-line @typescript-eslint/no-explicit-any
                break;
            case 'refreshUserData':
                session.refetch();
                break;
            case 'setDOM':
                if (data.selector && data.html && data.rect) {
                    if (document.getElementById('legacyElementFaker') !== null) {
                        document.getElementById('legacyElementFaker')!.remove();
                    }

                    const div = document.createElement('div');
                    div.id = 'legacyElementFaker';
                    div.innerHTML = data.html;
                    div.dataset.id = data.id;
                    div.style.position = 'absolute';
                    div.style.opacity = '0';
                    div.style.pointerEvents = 'none';
                    div.style.width = data.rect.width + 'px';
                    div.style.height = data.rect.height + 'px';

                    const topValue = Number(
                        data.rect.top + document.getElementById('oldPageIframe')!.getBoundingClientRect().top,
                    );
                    div.style.top = topValue + 'px';
                    div.style.left = data.rect.left + 'px';
                    div.style.overflow = 'hidden';

                    const rectHeight = Number(data.rect.height);
                    if (topValue + rectHeight > window.innerHeight) {
                        const outsideMargin = topValue + rectHeight - window.innerHeight;
                        if (rectHeight > outsideMargin) {
                            div.style.height = rectHeight - outsideMargin + 'px';
                        } else {
                            div.style.height = '10px';
                            div.style.top = window.innerHeight - 10 + 'px';
                        }
                    }

                    document.body.appendChild(div);
                }
                break;
            //TODO Commented while a solution is found for tours
            /* case 'tourController':
                if (data.tour) {
                    TourController.load(data.tour, (data) => sendMessage({ type: 'tourController', data }));
                }
                break;
            case 'tourController.status':
                sendMessage({ type: 'tourController.status', data: TourController.status() });
                break;
            case 'tourController.setStatus':
                if (data.tour && data.status) {
                    TourController.setStatus(data.status ? 'true' : 'false', data.tour);
                }
                break; */
            case 'popup': {
                const showPopup: boolean = isset(data.value, false);
                setShowBackdrop(showPopup);
                break;
            }
            case 'postHogSetPropertyFlags':
                if (data.set) {
                    posthog.setPersonPropertiesForFlags(data.set);
                    posthog.reloadFeatureFlags();
                }
                break;
            case 'logout':
                session.logout.logout();
                break;
            case 'upgrade-plan':
                navigate('/settings/workspace/billing', { replace: true });
                break;
            default:
                break;
        }
    };

    const lastPath = useRef('');

    const syncPaths = () => {
        const path = getPagePath(null, isLegacy === 'old' ? 'new' : 'old', false);
        if (lastPath.current !== path) {
            if (isLegacy) {
                if (isLegacy === 'new') {
                    sendMessage({ path: path });
                } else {
                    navigate(path, { replace: true });
                }
            }
            lastPath.current = path;
        }
    };

    // When the type is old, it means that the path is from the new app and you want to receive the old path
    // When the type is new, it means that the path is from the old app and you want to receive the new path
    const getPagePath = (path?: string | null, getType?: string, setState?: boolean) => {
        const type = getType || 'new';
        const inverseType = type === 'old' ? 'new' : 'old';
        const auxPath = path || pathName;
        let finalPath = null;

        setState = isset(setState, true);

        const allReactPaths = JSON.parse(JSON.stringify(reactPaths));

        let excludePath = false;
        for (const tempExcludePath of excludePaths) {
            if ((path || pathName).includes(tempExcludePath)) {
                excludePath = true;
            }
        }

        const invertedTypeIndex = (inverseType + 'Path') as keyof LegacyPath;
        const typeIndex = (type + 'Path') as keyof LegacyPath;

        if (!(allReactPaths.indexOf(auxPath) !== -1 && !excludePath)) {
            for (const paths of pathMatch) {
                if (auxPath.startsWith('/' + paths[invertedTypeIndex])) {
                    if (typeof paths[typeIndex] === 'string') {
                        finalPath = auxPath.replace('/' + paths[invertedTypeIndex], '/' + paths[typeIndex]);
                        break;
                    }
                }
            }
        }

        if (setState) {
            preventHistoryRender.current = true;
            setTimeout(() => (preventHistoryRender.current = false), 100);
        }

        if (finalPath) {
            return finalPath;
        }

        return lastPath.current || (isLegacy ? auxPath : '/' + pathMatch[0][typeIndex]);
    };

    const preventHistoryRender = useRef(true);

    useEffect(() => {
        if (!preventHistoryRender.current) {
            syncPaths();
        }
    });

    const initialSrc = useRef(target + getPagePath(null, 'old', false));

    // Check if the user has access to the current path
    if (!usersPathFilterAccess(pathName, session.workspace)) {
        return <NotFound />;
    }

    return (
        <iframe
            title="Page"
            id="legacyPageIframe"
            allow="clipboard-write"
            width={'100%'}
            height={'100%'}
            src={initialSrc.current}
            ref={setIframeNode}
            style={{
                border: '0px solid',
                flex: '1 1 auto',
                ...(showBackdrop && {
                    zIndex: 1401,
                }),
            }}
            onLoad={() => {
                /* isIframeLoaded = true;
                setPostHogIdentify(); */
            }}
        ></iframe>
    );
};

export default LegacyIframe;

type SendMessageSet = {
    name: string;
    email: string;
    activeTeam: number | null;
    language: string;
    teams: Array<number>;
};
type SendMessageData = {
    type?: string;
    path?: string;
    id?: string;
    set?: SendMessageSet;
    selector?: string;
    data?: CallCallbackParams | { name: string | null; running: boolean };
};

export function sendMessage(data: SendMessageData) {
    if (iframe) {
        iframe.postMessage(data, legacyHost);
    }
}

export function deleteFakeElement() {
    if (document.getElementById('legacyElementFaker') !== null) {
        document.getElementById('legacyElementFaker')!.remove();
    }
}
